"use client";

import { useState, useEffect, useRef } from "react";
import { accent } from "@/lib/palette";
import { posthog } from "@/lib/posthog";

/** Button that opens the waitlist modal. Accepts className and style for flexible placement. */
export function WaitlistButton({ className, style, children }: {
  className?: string;
  style?: React.CSSProperties;
  children?: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={() => document.dispatchEvent(new CustomEvent("open-waitlist"))}
      className={className}
      style={style}
    >
      {children ?? "Join Waitlist"}
    </button>
  );
}

/** Modal with waitlist form. Render once at the layout level. */
export default function WaitlistModal() {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [linkedin, setLinkedin] = useState("");
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [errorMsg, setErrorMsg] = useState("");
  const backdropRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = () => setOpen(true);
    document.addEventListener("open-waitlist", handler);
    return () => document.removeEventListener("open-waitlist", handler);
  }, []);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [open]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setStatus("loading");
    setErrorMsg("");

    try {
      const res = await fetch("/api/waitlist", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim(), linkedin: linkedin.trim() || undefined }),
      });
      const data = await res.json();

      if (data.success) {
        setStatus("success");
        posthog.capture("waitlist_signup", { email: email.trim() });
      } else {
        setStatus("error");
        setErrorMsg(data.error || "Something went wrong");
      }
    } catch {
      setStatus("error");
      setErrorMsg("Network error. Please try again.");
    }
  }

  if (!open) return null;

  return (
    <div
      ref={backdropRef}
      className="fixed inset-0 z-[100] flex items-center justify-center px-4"
      style={{ backgroundColor: "rgba(0,0,0,0.6)", backdropFilter: "blur(4px)" }}
      onClick={(e) => { if (e.target === backdropRef.current) setOpen(false); }}
    >
      <div
        className="relative w-full max-w-md rounded-2xl p-8"
        style={{ backgroundColor: "#16112a", border: `1px solid ${accent.ring}` }}
      >
        {/* Close button */}
        <button
          onClick={() => setOpen(false)}
          className="absolute top-4 right-4 p-1 rounded-lg transition-colors"
          style={{ color: accent.dim }}
          aria-label="Close"
        >
          <svg width="20" height="20" viewBox="0 0 16 16" fill="none">
            <path d="M4 4L12 12M12 4L4 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
        </button>

        {status === "success" ? (
          <div className="text-center py-4">
            <div
              className="inline-flex items-center gap-2 px-6 py-3 rounded-lg text-sm font-semibold mb-3"
              style={{ backgroundColor: accent.subtle, color: accent.DEFAULT }}
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M3 8.5L6.5 12L13 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              You&rsquo;re on the list!
            </div>
            <p className="text-sm" style={{ color: accent.dim }}>
              We&rsquo;ll reach out when your spot is ready.
            </p>
          </div>
        ) : (
          <>
            <h2 className="text-xl font-bold text-white mb-1">Join the Waitlist</h2>
            <p className="text-sm mb-6" style={{ color: accent.dim }}>
              Get early access to the Qosm workspace and capability runtime.
            </p>

            <form onSubmit={handleSubmit} className="flex flex-col gap-3">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@email.com"
                required
                autoFocus
                className="w-full px-4 py-2.5 rounded-lg text-sm border-none outline-none"
                style={{ backgroundColor: "rgba(255,255,255,0.08)", color: "#fff" }}
              />
              <input
                type="text"
                value={linkedin}
                onChange={(e) => setLinkedin(e.target.value)}
                placeholder="LinkedIn profile (optional)"
                className="w-full px-4 py-2.5 rounded-lg text-sm border-none outline-none"
                style={{ backgroundColor: "rgba(255,255,255,0.05)", color: "rgba(255,255,255,0.7)" }}
              />
              <button
                type="submit"
                disabled={status === "loading"}
                className="w-full px-6 py-2.5 rounded-lg text-sm font-semibold transition-all hover:-translate-y-px disabled:opacity-60"
                style={{ backgroundColor: accent.DEFAULT, color: accent.onAccent }}
              >
                {status === "loading" ? "Joining..." : "Join Waitlist"}
              </button>
              {status === "error" && (
                <p className="text-xs text-center" style={{ color: "#f87171" }}>{errorMsg}</p>
              )}
            </form>
          </>
        )}
      </div>
    </div>
  );
}
